/**
 * Frontend JavaScript for the Pricing Calculator block.
 * Handles interactive functionality on the frontend.
 */

/**
 * Calculation function for pricing calculator.
 * @param {number} awsCost
 * @param {number} computePercent
 * @param {Object} regionData
 */
export function calculateResults( awsCost, computePercent, regionData ) {
	const computeCost = ( awsCost * computePercent ) / 100;
	const co2Emission = ( computeCost / regionData.co2PerDollar ) * 17;
	const potentialSavings = ( computeCost * regionData.savingsPercent ) / 100;
	const co2Reduction = ( co2Emission / 100 ) * 70;
	const trees = co2Reduction / 12;

	return {
		computeCost: computeCost.toFixed( 2 ),
		co2Emission: co2Emission.toFixed( 2 ),
		potentialSavings: potentialSavings.toFixed( 2 ),
		co2Reduction: co2Reduction.toFixed( 2 ),
		trees: Math.round( trees ),
	};
}

/**
 * Update display values for sliders.
 * @param {HTMLElement} slider
 * @param {string}      value
 */
function updateSliderDisplay( slider, value ) {
	const display = slider.parentElement.querySelector( '[data-display]' );
	if ( ! display ) {
		return;
	}

	const sliderType = display.getAttribute( 'data-display' );
	if ( sliderType === 'slider1' ) {
		display.textContent = '$' + parseInt( value ).toLocaleString();
	} else if ( sliderType === 'slider2' ) {
		display.textContent = value + '%';
	}

	// Update progress background for WebKit browsers.
	updateSliderProgress( slider, value );
}

/**
 * Update slider progress background.
 * @param {HTMLElement} slider
 * @param {string}      value
 */
function updateSliderProgress( slider, value ) {
	const min = slider.min || 0;
	const max = slider.max || 100;
	const percentage = ( ( value - min ) / ( max - min ) ) * 100;

	// Create gradient background showing progress using theme colors.
	const progressGradient = `linear-gradient(to right, var(--color-primary-blue) 0%, var(--color-primary-blue) ${ percentage }%, var(--color-gray-light) ${ percentage }%, var(--color-gray-light) 100%)`;
	slider.style.background = progressGradient;
}

/**
 * Update all result displays.
 * @param {HTMLElement} calculator
 * @param {Object}      results
 */
function updateResults( calculator, results ) {
	const resultElements = {
		computeCost: calculator.querySelector( '[data-result="computeCost"]' ),
		co2Emission: calculator.querySelector( '[data-result="co2Emission"]' ),
		potentialSavings: calculator.querySelector(
			'[data-result="potentialSavings"]'
		),
		co2Reduction: calculator.querySelector(
			'[data-result="co2Reduction"]'
		),
		trees: calculator.querySelector( '[data-result="trees"]' ),
	};

	if ( resultElements.computeCost ) {
		resultElements.computeCost.textContent = '$ ' + results.computeCost;
	}
	if ( resultElements.co2Emission ) {
		resultElements.co2Emission.textContent =
			'kg CO₂-eq ' + results.co2Emission;
	}
	if ( resultElements.potentialSavings ) {
		resultElements.potentialSavings.textContent =
			'$ ' + results.potentialSavings;
	}
	if ( resultElements.co2Reduction ) {
		resultElements.co2Reduction.textContent =
			'kg CO₂-eq ' + results.co2Reduction;
	}
	if ( resultElements.trees ) {
		resultElements.trees.textContent = results.trees;
	}
}

/**
 * Get current calculator values.
 * @param {HTMLElement} calculator
 */
function getCalculatorValues( calculator ) {
	const slider1 = calculator.querySelector( '[data-slider="slider1"]' );
	const slider2 = calculator.querySelector( '[data-slider="slider2"]' );
	const regionRadios = calculator.querySelectorAll(
		'[data-selector="region"]'
	);

	// Get config data.
	const config = JSON.parse(
		calculator.getAttribute( 'data-config' ) || '{}'
	);
	const regions = config.regions || [];

	// Find selected region radio button.
	let selectedRegionValue = regions[ 0 ]?.value;
	regionRadios.forEach( ( radio ) => {
		if ( radio.checked ) {
			selectedRegionValue = radio.value;
		}
	} );

	const selectedRegion = regions.find(
		( r ) => r.value === selectedRegionValue
	) ||
		regions[ 0 ] || { savingsPercent: 70, co2PerDollar: 0.5 };

	return {
		awsCost: slider1 ? parseInt( slider1.value ) : 0,
		computePercent: slider2 ? parseInt( slider2.value ) : 0,
		regionData: selectedRegion,
	};
}

/**
 * Recalculate and update all displays.
 * @param {HTMLElement} calculator
 */
function recalculate( calculator ) {
	const values = getCalculatorValues( calculator );
	const results = calculateResults(
		values.awsCost,
		values.computePercent,
		values.regionData
	);
	updateResults( calculator, results );
}

/**
 * Initialize a pricing calculator.
 * @param {HTMLElement} calculator
 */
function initCalculator( calculator ) {
	// Get all interactive elements.
	const sliders = calculator.querySelectorAll( '[data-slider]' );

	// Get config data to access defaults.
	const config = JSON.parse(
		calculator.getAttribute( 'data-config' ) || '{}'
	);

	// Add event listeners for sliders.
	sliders.forEach( ( slider ) => {
		slider.addEventListener( 'input', function () {
			updateSliderDisplay( this, this.value );
			recalculate( calculator );
		} );

		// Set slider to its default value if not already set properly.
		const sliderType = slider.getAttribute( 'data-slider' );
		if ( sliderType === 'slider1' && config.slider1Default ) {
			slider.value = config.slider1Default;
		} else if ( sliderType === 'slider2' && config.slider2Default ) {
			slider.value = config.slider2Default;
		}

		// Initialize display.
		updateSliderDisplay( slider, slider.value );
	} );

	// Add event listeners for region radio buttons.
	const regionRadios = calculator.querySelectorAll(
		'[data-selector="region"]'
	);
	regionRadios.forEach( ( radio ) => {
		radio.addEventListener( 'change', function () {
			recalculate( calculator );
		} );
	} );

	// Ensure first radio button is checked by default if none are checked.
	const checkedRadio = calculator.querySelector(
		'[data-selector="region"]:checked'
	);
	if ( ! checkedRadio && regionRadios.length > 0 ) {
		regionRadios[ 0 ].checked = true;
	}

	// Initial calculation
	recalculate( calculator );
}

/**
 * Initialize all pricing calculators on the page.
 */
function initPricingCalculators() {
	const calculators = document.querySelectorAll( '.pricing-calculator' );
	calculators.forEach( ( calculator ) => {
		initCalculator( calculator );
	} );
}

// Initialize when DOM is ready.
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initPricingCalculators );
} else {
	initPricingCalculators();
}
